import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './providers/AuthProvider';
import { useResponsiveTableLabels } from './lib/responsiveTables';
import { PortalLayout } from './components/layout/PortalLayout';
import { AdminLayout } from './components/layout/AdminLayout';
import { Login } from './pages/auth/Login';
import { MagicLink } from './pages/auth/MagicLink';
import { Dashboard } from './pages/portal/Dashboard';
import { Lease } from './pages/portal/Lease';
import { Ledger } from './pages/portal/Ledger';
import { Pay } from './pages/portal/Pay';
import { Documents } from './pages/portal/Documents';
import { Maintenance } from './pages/portal/Maintenance';
import { Messages } from './pages/portal/Messages';
import { PaymentMethods } from './pages/portal/PaymentMethods';
import { AdminDashboard } from './pages/admin/Dashboard';
import { AdminProperties } from './pages/admin/Properties';
import { AdminTenants } from './pages/admin/Tenants';
import { AdminLeases } from './pages/admin/Leases';
import { AdminLeaseDetail } from './pages/admin/LeaseDetail';
import { AdminMoveOut } from './pages/admin/MoveOut';
import { AdminWorkOrders } from './pages/admin/WorkOrders';
import { AdminDocuments } from './pages/admin/Documents';
import { AdminMessages } from './pages/admin/Messages';
import { AdminPaymentMethods } from './pages/admin/PaymentMethodsAdmin';
import { AdminAccount } from './pages/admin/Account';
import { AdminStaff } from './pages/admin/Staff';
import { AdminContractors } from './pages/admin/Contractors';
import { AdminAuditLog } from './pages/admin/AuditLog';
import { AdminPersonActivity } from './pages/admin/PersonActivity';
import { ContractorLayout } from './components/layout/ContractorLayout';
import { ContractorJobs } from './pages/contractor/Jobs';
import { ContractorJobDetail } from './pages/contractor/JobDetail';
import { NotFound } from './pages/system/NotFound';
import { homeFor, SHELL_ROLES } from './lib/roles';

/** A full-page loading state while the session resolves (keeps landmarks + a single h1). */
function AppLoading() {
  return (
    <main id="main-content" tabIndex={-1}>
      <h1 className="visually-hidden">Loading</h1>
      <p className="status" role="status" aria-live="polite">
        Loading…
      </p>
    </main>
  );
}

/**
 * Gate a shell to authenticated users whose role the shell ADMITS (positive allow-list, 0.25.0 —
 * it used to be "admin unless tenant", which would have let a contractor into /admin). Anyone else
 * is redirected to their own home shell (a contractor hitting /admin or /portal lands on
 * /contractor). The API enforces the same rules server-side; this is UX, not the security boundary.
 */
function Protected({ role, children }: { role: 'tenant' | 'admin' | 'contractor'; children: ReactNode }) {
  const { status, principal } = useAuth();
  if (status === 'loading') return <AppLoading />;
  if (status === 'anonymous' || !principal) return <Navigate to="/login" replace />;
  if (!SHELL_ROLES[role].includes(principal.role)) return <Navigate to={homeFor(principal.role)} replace />;
  return <>{children}</>;
}

/** Route "/" to the correct shell (or login) based on the resolved principal. */
function HomeRedirect() {
  const { status, principal } = useAuth();
  if (status === 'loading') return <AppLoading />;
  if (!principal) return <Navigate to="/login" replace />;
  return <Navigate to={homeFor(principal.role)} replace />;
}

export function App() {
  // Auto-label data tables + keep ARIA table roles for the mobile card-stack (styles.css ≤640px).
  useResponsiveTableLabels();
  return (
    <Routes>
      <Route path="/" element={<HomeRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/auth/magic" element={<MagicLink />} />

      {/* Tenant portal shell */}
      <Route
        path="/portal"
        element={
          <Protected role="tenant">
            <PortalLayout />
          </Protected>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="lease" element={<Lease />} />
        <Route path="ledger" element={<Ledger />} />
        <Route path="pay" element={<Pay />} />
        <Route path="documents" element={<Documents />} />
        <Route path="maintenance" element={<Maintenance />} />
        <Route path="messages" element={<Messages />} />
        <Route path="payment-methods" element={<PaymentMethods />} />
      </Route>

      {/* Owner/staff admin shell */}
      <Route
        path="/admin"
        element={
          <Protected role="admin">
            <AdminLayout />
          </Protected>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="properties" element={<AdminProperties />} />
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="leases" element={<AdminLeases />} />
        <Route path="leases/:leaseId" element={<AdminLeaseDetail />} />
        <Route path="leases/:leaseId/move-out" element={<AdminMoveOut />} />
        <Route path="work-orders" element={<AdminWorkOrders />} />
        <Route path="documents" element={<AdminDocuments />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="payment-methods" element={<AdminPaymentMethods />} />
        <Route path="account" element={<AdminAccount />} />
        <Route path="staff" element={<AdminStaff />} />
        <Route path="contractors" element={<AdminContractors />} />
        <Route path="audit" element={<AdminAuditLog />} />
        <Route path="activity/:kind/:id" element={<AdminPersonActivity />} />
      </Route>

      {/* Contractor job app (0.25.0; mobile-first) */}
      <Route
        path="/contractor"
        element={
          <Protected role="contractor">
            <ContractorLayout />
          </Protected>
        }
      >
        <Route index element={<ContractorJobs />} />
        <Route path="jobs/:id" element={<ContractorJobDetail />} />
      </Route>

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
